/**
 * DeleteButton Component
 *
 * Reusable delete button that can be used in:
 * - Modal contexts (with state and props)
 * - Direct contexts (with assetId)
 * - Any component that needs delete functionality
 */

import React from 'react'
import deleteImg from '../../../assets/images/delete2.svg'
import { useAssetDeletion } from './hooks/useAssetDeletion'

const DeleteButton = ({
  // Modal context props
  state = null,
  props = null,
  onClose = null,
  // Direct context props
  assetId = null,
  userId = null,
  // UI customization
  className = 'button-med delete-button',
  iconClassName = 'delete-img-btn',
  showIcon = true,
  children = null,
  // Behavior
  onSuccess = null,
  onError = null,
  disabled = false,
  // Visibility control
  autoHide = true
}) => {
  const { deleteAssetById, handleDeleteAsset, getDeleteButtonVisibility } =
    useAssetDeletion()

  const shouldShow = autoHide ? getDeleteButtonVisibility() : true

  if (!shouldShow) {
    return null
  }

  const handleClick = async () => {
    if (disabled) return

    let success = false

    // Modal context - use state and props
    if (state && props) {
      success = await handleDeleteAsset(state, props, onClose)
    }
    // Direct context - use assetId directly
    else if (assetId) {
      const onSuccessCallback = deletedAssetId => {
        if (onSuccess) {
          onSuccess(deletedAssetId)
        }
      }
      success = await deleteAssetById(assetId, userId, onSuccessCallback)
    } else {
      console.error(
        'DeleteButton: Either (state + props) or assetId must be provided'
      )
      return
    }

    if (!success && onError) {
      onError(assetId)
    }
  }

  return (
    <button
      onClick={handleClick}
      className={className}
      disabled={disabled}
      aria-label="Delete asset"
    >
      {showIcon && (
        <img src={deleteImg} alt="Delete" className={iconClassName} />
      )}
      {children}
    </button>
  )
}

export default DeleteButton
